import { areFingerprintsEqual, stableJsonStringify } from '../../../shared/lib/stableFingerprint';

export type ValidationRunSignatureInput = Record<string, unknown>;

type CacheEntry<TReport> = {
  report: TReport;
  storedAt: number;
};

export function buildValidationRunSignature(input: ValidationRunSignatureInput): string {
  return stableJsonStringify(input);
}

export interface ValidationRunSignatureContext {
  mode: string;
  documentName: string | null;
  documentPath: string | null;
  activeProjectKey: string | null;
  verifiedRevision: string | null;
  validationIds: number[];
  runSettings: Record<string, unknown>;
  preflightSettings: Record<string, unknown>;
  mappingOverrides: Record<string, string>;
}

/**
 * The two cache keys a run is filed under. The preflight key stays free of mode,
 * revision and selection because the host mapping sync depends only on the
 * document and the Revit payload.
 */
export function buildValidationRunSignatures(
  context: ValidationRunSignatureContext
): { runSignature: string; preflightSignature: string } {
  const documentContext = {
    documentName: context.documentName,
    documentPath: context.documentPath,
    activeProjectKey: context.activeProjectKey,
  };

  return {
    runSignature: buildValidationRunSignature({
      ...documentContext,
      mode: context.mode,
      verifiedRevision: context.verifiedRevision,
      validationIds: context.validationIds,
      settings: context.runSettings,
      mappingOverrides: context.mappingOverrides,
    }),
    preflightSignature: buildValidationRunSignature({
      ...documentContext,
      settings: context.preflightSettings,
      mappingOverrides: context.mappingOverrides,
    }),
  };
}

// Bumped by every cache invalidation, no matter which coordinator instance or
// hook triggered it. A run snapshots it at start and refuses to commit when it
// moved, so a result captured before an invalidation can never overwrite
// fresher evidence — including invalidations raised for a different document.
let validationInvalidationEpoch = 0;

export function getValidationInvalidationEpoch(): number {
  return validationInvalidationEpoch;
}

export interface SupersededRunProbe {
  invalidationEpochAtStart: number;
  runContextFingerprint: string;
  liveContextFingerprint: () => string;
}

/** Why an in-flight run's result must be discarded, or null when it may commit. */
export function getSupersededRunReason(probe: SupersededRunProbe): string | null {
  if (validationInvalidationEpoch !== probe.invalidationEpochAtStart) {
    return 'the model was invalidated while the run was in flight';
  }

  if (!areFingerprintsEqual(probe.liveContextFingerprint(), probe.runContextFingerprint)) {
    return 'the validation context changed while the run was in flight';
  }

  return null;
}

export class ValidationRunCoordinator<TReport> {
  private readonly reportCache = new Map<string, CacheEntry<TReport>>();
  private readonly inFlightRuns = new Map<string, Promise<TReport | null>>();
  private readonly syncedPreflightSignatures = new Set<string>();

  getCachedReport(signature: string): TReport | null {
    return this.reportCache.get(signature)?.report ?? null;
  }

  storeReport(signature: string, report: TReport): void {
    this.reportCache.set(signature, {
      report,
      storedAt: Date.now(),
    });
  }

  shouldSyncPreflight(signature: string): boolean {
    return !this.syncedPreflightSignatures.has(signature);
  }

  markPreflightSynced(signature: string): void {
    this.syncedPreflightSignatures.add(signature);
  }

  run(signature: string, forceFresh: boolean, runner: () => Promise<TReport | null>): Promise<TReport | null> {
    if (!forceFresh) {
      const cached = this.getCachedReport(signature);
      if (cached) {
        return Promise.resolve(cached);
      }

      const inFlight = this.inFlightRuns.get(signature);
      if (inFlight) {
        return inFlight;
      }
    }

    const promise = runner().finally(() => {
      this.inFlightRuns.delete(signature);
    });
    this.inFlightRuns.set(signature, promise);
    return promise;
  }

  invalidateReports(): void {
    this.reportCache.clear();
    this.inFlightRuns.clear();
    validationInvalidationEpoch++;
  }

  invalidateAll(): void {
    this.invalidateReports();
    this.syncedPreflightSignatures.clear();
  }
}
